import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { BiteAttempt } from "../../types/data";
import { BiteStatus } from "../../types/enums";
import {
  BITE_TYPE,
  type BiteType,
} from "@/features/notepad/constants/teaching";
import api from "../../../../utils/axios-utils";
import { getScreenshotUrl } from "../../utils/screenshotCache";
import { parseAttemptId } from "../../utils/attemptId";
import NotepadTranscriptView from "./NotepadTranscriptView";
import "./support-transcripts.css";
import { MathText } from "@ever-tutor/et-ui";

const NotepadSnapshotView = lazy(() => import("./NotepadSnapshotView"));

type SupportTranscriptsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  lessonName: string;
  biteIndex: number;
  attempt: BiteAttempt | null;
  biteType?: BiteType;
  stats?: {
    studentAsked: number;
    everTutorAsked: number;
    timeSpent: string;
    incorrectResponses: number;
  };
  instances?: SupportInstance[];
};

type TranscriptStep = {
  id: string;
  role: "student" | "tutor" | "system";
  label: string;
  text: string;
  status: "wrong" | "support" | "correct";
};

type SupportInstance = {
  id?: string;
  title: string;
  steps: TranscriptStep[];
};

type TranscriptsResponse = {
  attempt_id: string;
  instances: SupportInstance[];
};

const SupportTranscriptsModal = ({
  isOpen,
  onClose,
  studentName,
  lessonName,
  biteIndex,
  attempt,
  biteType,
  stats,
  instances,
}: SupportTranscriptsModalProps) => {
  const titleId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [transcripts, setTranscripts] = useState<TranscriptsResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [snapshotAvailable, setSnapshotAvailable] = useState<boolean | null>(
    null,
  );

  const isIncomplete = attempt?.biteStatus === BiteStatus.INCOMPLETE;
  // A practice attempt's work IS its canvas, so the snapshot sits inline above
  // the transcript rather than behind the screenshot toggle.
  const isPractice = biteType === BITE_TYPE.PRACTICE;
  // The last segment pins the snapshot row for THIS attempt, so re-attempts
  // don't all show the newest board. See attemptId.ts.
  const {
    sessionId: attemptSessionId,
    slideIdx: attemptSlideIdx,
    slideEndTs: attemptSlideEndTs,
  } = parseAttemptId(attempt?.id);

  useEffect(() => {
    let active = true;
    setTranscripts(null);
    setError(false);
    setIsLoading(false);
    if (isOpen && attempt?.id && !isIncomplete && !isPractice && !instances) {
      setIsLoading(true);
      api
        .get(`/analytics/transcripts/${attempt.id}`)
        .then((response) => {
          if (active) setTranscripts(response.data);
        })
        .catch(() => {
          if (active) setError(true);
        })
        .finally(() => {
          if (active) setIsLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [isOpen, attempt?.id, isIncomplete, isPractice, instances, retry]);

  useEffect(() => {
    let active = true;
    setSnapshotUrl(null);
    setShowSnapshot(false);
    setSnapshotAvailable(null);
    if (isOpen && attempt?.id && !isPractice) {
      getScreenshotUrl(attemptSessionId, attemptSlideIdx)
        .then((url) => {
          if (active) {
            setSnapshotUrl(url);
            setSnapshotAvailable(url !== null);
          }
        })
        .catch(() => {
          if (active) setSnapshotAvailable(false);
        });
    }
    return () => {
      active = false;
    };
  }, [isOpen, attempt?.id, isPractice, attemptSessionId, attemptSlideIdx]);

  // Toggling unmounts the focused button; hand focus to the toggle that
  // replaces it so keyboard users keep their place.
  const snapshotToggleRef = useRef<HTMLButtonElement>(null);
  const shouldFocusToggle = useRef(false);
  const handleViewSnapshot = () => {
    shouldFocusToggle.current = true;
    setShowSnapshot((value) => !value);
  };
  useEffect(() => {
    if (!shouldFocusToggle.current) return;
    shouldFocusToggle.current = false;
    snapshotToggleRef.current?.focus();
  }, [showSnapshot]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const popoverId = previous?.closest('[role="dialog"]')?.id;
    const biteTrigger = popoverId
      ? Array.from(
          document.querySelectorAll<HTMLElement>("[aria-controls]"),
        ).find((el) => el.getAttribute("aria-controls") === popoverId)
      : undefined;
    const overflow = document.body.style.overflow;
    const siblings = Array.from(document.body.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== overlayRef.current,
    );
    const inertStates = siblings.map((el) => el.inert);
    siblings.forEach((el) => {
      el.inert = true;
    });
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      siblings.forEach((el, index) => {
        el.inert = inertStates[index];
      });
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
      else if (biteTrigger?.isConnected) biteTrigger.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
      if (event.key !== "Tab") return;
      const stops = drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
      );
      const first = stops?.[0],
        last = stops?.[stops.length - 1];
      if (!first || !last) {
        event.preventDefault();
        drawerRef.current?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayStats = stats ?? attemptStats(attempt);
  const supportInstances = transcripts?.instances ?? instances ?? [];

  return createPortal(
    <div ref={overlayRef} className="et-support-overlay">
      {/* Translucent backdrop */}
      <div
        className="et-support-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Full-height support drawer, matching the analytics reference. */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={showSnapshot ? undefined : titleId}
        aria-label={
          showSnapshot
            ? `${lessonName} (Bite ${biteIndex}) snapshot`
            : undefined
        }
        tabIndex={-1}
        className="et-support-drawer"
      >
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="et-support-close"
        >
          <X size={14} />
        </button>

        <div className="et-support-content">
          {showSnapshot && snapshotUrl ? (
            /* ---- Snapshot full-modal view ---- */
            <div className="et-support-snapshot-view">
              <button
                ref={snapshotToggleRef}
                type="button"
                className="et-support-snapshot-toggle"
                onClick={handleViewSnapshot}
              >
                Back to support transcripts
              </button>
              <img
                src={snapshotUrl}
                alt="Bite snapshot"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            /* ---- Normal transcript view ---- */
            <>
              <div className="et-support-header">
                <div className="et-support-heading">
                  <p className="et-support-eyebrow">
                    Support transcripts of {studentName} and EverTutor
                  </p>
                  <div className="et-support-summary">
                    <div>
                      <h2 id={titleId}>
                        {lessonName} (Bite {biteIndex})
                      </h2>
                      {snapshotAvailable && (
                        <button
                          ref={snapshotToggleRef}
                          type="button"
                          className="et-support-snapshot-toggle"
                          onClick={handleViewSnapshot}
                        >
                          <span className="font-mulish font-bold text-[15px] leading-none text-[#252525]">
                            View Bite Snapshot
                          </span>
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 9 9"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.50262 2.55603L1.05865 9L0 7.94135L6.44322 1.49738H0.764413V0H9V8.23559H7.50262V2.55603Z"
                              fill="#252525"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <SupportStats
                      displayStats={displayStats}
                      isPractice={isPractice}
                      hasInstructionalHelp={attempt?.hasInstructionalHelp}
                    />
                  </div>
                </div>
              </div>

              {/* Scrollable transcripts section */}
              <div className="et-support-body">
                <TranscriptContent
                  isPractice={isPractice}
                  sessionId={attemptSessionId}
                  slideIdx={attemptSlideIdx}
                  slideEndTs={attemptSlideEndTs}
                  studentName={studentName}
                  isIncomplete={isIncomplete}
                  isLoading={isLoading}
                  error={error}
                  supportInstances={supportInstances}
                  onRetry={() => setRetry((value) => value + 1)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const attemptStats = (attempt: BiteAttempt | null) => ({
  studentAsked: attempt?.studentPerformance?.studentAsked ?? 0,
  everTutorAsked: attempt?.studentPerformance?.AIAsked ?? 0,
  timeSpent: formatTimeSpent(attempt?.studentPerformance?.timeSpent ?? 0),
  incorrectResponses: attempt?.studentPerformance?.incorrectAttempts ?? 0,
});
const SupportStats = ({
  displayStats,
  isPractice,
  hasInstructionalHelp,
}: {
  displayStats: NonNullable<SupportTranscriptsModalProps["stats"]>;
  isPractice: boolean;
  hasInstructionalHelp?: boolean;
}) => (
  <div className="et-support-stats">
    {[
      // The "Asked" counters come from QAR initiated_party,
      // which only the tutor pipeline produces. On a practice
      // bite they are structurally 0, and "0 Questions" reads
      // as an observation rather than "not applicable".
      ...(isPractice
        ? []
        : [
            {
              label: "Student Asked",
              value: `${displayStats.studentAsked} Questions`,
            },
            {
              label: "EverTutor Asked",
              value: `${displayStats.everTutorAsked} Questions`,
            },
          ]),
      { label: "Time Spent", value: displayStats.timeSpent },
      {
        label: "Incorrect responses",
        value: displayStats.incorrectResponses,
      },
      // Only when actually measured: the classifiers run on
      // completed bites only, so undefined means "unknown",
      // not "no help given".
      ...(isPractice && hasInstructionalHelp !== undefined
        ? [
            {
              label: "Instructional help",
              value: hasInstructionalHelp ? "Given" : "None",
            },
          ]
        : []),
    ].map((item) => (
      <div key={item.label} className="et-support-stat">
        <div className="et-support-stat-label">{item.label}</div>
        <div className="et-support-stat-value">{item.value}</div>
      </div>
    ))}
  </div>
);

const TranscriptInstances = ({
  instances,
  studentName,
}: {
  instances: SupportInstance[];
  studentName: string;
}) => (
  <div className="et-support-instances">
    {instances.map((instance, idx) => (
      <div key={instance.id || `instance-${idx}`}>
        <p className="mb-[15px] text-sm font-medium text-gray-400 italic">
          {instance.title ||
            `${idx + 1}${getOrdinalSuffix(idx + 1)} Instance of support`}
        </p>
        <div className="et-support-conversation">
          <div className="space-y-[15px]">
            {instance.steps.map((step) => {
              const isTutorOrSystem =
                step.role === "tutor" || step.role === "system";
              return (
                <div key={step.id} className="et-support-step">
                  <div className="relative flex-shrink-0">
                    {isTutorOrSystem ? (
                      // Tutor/System avatar
                      <div className="h-10 w-10 rounded-full overflow-hidden bg-[#B3F285]/40 flex items-center justify-center">
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 43 42"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.7071 13.709C11.5136 17.9402 14.0022 30.0306 17.0516 32.6683C21.4736 36.4995 28.575 36.7203 31.7916 31.4389C34.9128 26.3187 31.3918 10.6178 28.0023 8.23079C23.0014 4.71584 14.4616 7.46097 12.7071 13.709Z"
                            fill="#EFF6F5"
                          />
                          <path
                            d="M17.2578 32.4283C19.4076 34.2934 22.209 35.2752 24.8549 35.1524C27.4933 35.0298 29.9727 33.8113 31.5231 31.2733C32.0364 30.4318 32.3709 29.2618 32.5438 27.8677C32.7164 26.4778 32.7255 24.8872 32.6043 23.221C32.3615 19.8878 31.5988 16.2821 30.6053 13.429C30.1826 12.2111 29.7196 11.1355 29.2413 10.2814C28.7588 9.42007 28.2767 8.8132 27.8279 8.49897C25.4283 6.81518 22.1531 6.61671 19.2462 7.61894C16.3413 8.62045 13.8599 10.8013 13.0165 13.8045L13.016 13.8064C12.6981 14.9103 12.643 16.6238 12.8071 18.61C12.9704 20.5866 13.3478 22.7999 13.8721 24.8853C14.3018 26.5894 14.8264 28.2025 15.4097 29.5316C15.9972 30.8702 16.6293 31.8849 17.2578 32.4283Z"
                            fill="white"
                            stroke="#101010"
                            strokeWidth="0.931227"
                          />
                          <path
                            d="M23.1851 20.708C23.1851 20.6781 23.1315 20.7617 23.1435 20.7975C23.3642 21.5972 23.4895 22.552 22.8212 22.9996C22.8212 22.9996 22.8212 23.0533 22.8212 23.0413C23.7044 22.7132 23.5254 21.448 23.1851 20.708Z"
                            fill="#263238"
                          />
                          <path
                            d="M22.2553 20.0581C20.9364 20.285 21.4735 22.9107 22.7028 22.7078C23.9322 22.5049 23.3653 19.8732 22.2553 20.0581Z"
                            fill="#263238"
                          />
                          <path
                            d="M27.8872 19.5574C27.8872 19.5216 27.9589 19.5573 27.9649 19.611C28.108 20.4346 28.4064 21.3477 29.2002 21.467C29.2002 21.467 29.2299 21.5147 29.2002 21.5147C28.2751 21.5744 27.9171 20.3511 27.8872 19.5574Z"
                            fill="#263238"
                          />
                          <path
                            d="M28.4423 18.5554C29.7313 18.1854 30.3698 20.7933 29.1763 21.1335C27.9829 21.4736 27.3622 18.8657 28.4423 18.5554Z"
                            fill="#263238"
                          />
                          <path
                            d="M21.0131 19.132C21.339 18.952 21.6539 18.7527 21.9561 18.5353C22.3201 18.3144 22.6305 18.1772 22.8094 17.7774C22.8508 17.6584 22.8509 17.5289 22.8098 17.4099C22.7687 17.2908 22.6887 17.189 22.5827 17.121C22.3546 17.0162 22.1028 16.9741 21.8531 16.9993C21.6033 17.0244 21.3649 17.1159 21.1623 17.2642C20.7075 17.5018 20.3567 17.8993 20.1777 18.3801C20.131 18.4939 20.1205 18.6195 20.1478 18.7395C20.1752 18.8595 20.239 18.9681 20.3305 19.0505C20.422 19.1328 20.5368 19.1848 20.6589 19.1995C20.7811 19.214 20.9048 19.1905 21.0131 19.132Z"
                            fill="#263238"
                          />
                          <path
                            d="M29.1471 17.1996C28.7712 17.1996 28.419 17.1638 28.0431 17.1399C27.6537 17.174 27.264 17.0796 26.9331 16.8713C26.8458 16.7826 26.7885 16.6685 26.7693 16.5455C26.75 16.4224 26.7698 16.2964 26.8257 16.1851C26.9786 15.9867 27.1809 15.8322 27.4124 15.7369C27.644 15.6415 27.8965 15.6088 28.1447 15.642C28.6581 15.6375 29.1551 15.8224 29.541 16.1612C29.6297 16.2418 29.6921 16.3472 29.72 16.4638C29.748 16.5803 29.7401 16.7025 29.6976 16.8145C29.6551 16.9266 29.5799 17.0231 29.4818 17.0918C29.3836 17.1605 29.267 17.1981 29.1471 17.1996Z"
                            fill="#263238"
                          />
                          <path
                            d="M24.0982 28.2809C24.337 28.4301 24.5816 28.6509 24.886 28.615C25.1862 28.5474 25.4688 28.4174 25.7155 28.2332C25.7202 28.2268 25.7273 28.2227 25.735 28.2215C25.7429 28.2204 25.7509 28.2224 25.7572 28.2272C25.7635 28.2319 25.7677 28.239 25.7688 28.2469C25.7699 28.2547 25.7679 28.2626 25.7631 28.269C25.6784 28.4517 25.5463 28.6084 25.3804 28.7228C25.2147 28.8372 25.0212 28.905 24.8203 28.9194C24.6438 28.9094 24.4744 28.8473 24.333 28.7412C24.1918 28.635 24.0853 28.4893 24.0266 28.3226C24.0265 28.3142 24.0287 28.3059 24.0329 28.2986C24.0369 28.2914 24.043 28.2853 24.0502 28.2809C24.0576 28.2767 24.0659 28.2746 24.0743 28.2746C24.0827 28.2744 24.091 28.2767 24.0982 28.2809Z"
                            fill="#263238"
                          />
                          <path
                            d="M24.4561 25.6669C24.7302 25.9456 25.0586 26.1652 25.4209 26.3119C25.7833 26.4587 26.1718 26.5297 26.5627 26.5203C26.903 26.5168 27.2409 26.4625 27.5653 26.3592C27.6289 26.3464 27.6911 26.3264 27.7502 26.2994C27.8098 26.2829 27.8677 26.2609 27.9233 26.2339C27.9664 26.2189 28.0027 26.1891 28.0258 26.1496C28.0487 26.1102 28.0568 26.0639 28.0486 26.019C28.0513 25.9913 28.0513 25.9632 28.0486 25.9355V25.8638C27.9352 25.2372 27.6786 24.2944 27.6786 24.2944C27.9352 24.36 29.2123 24.6464 29.1227 24.2944C28.4067 21.6149 27.6069 18.6371 26.324 16.1247C26.3148 16.1098 26.3008 16.0985 26.2843 16.0925C26.2679 16.0865 26.25 16.0864 26.2334 16.0918C26.2169 16.0974 26.2026 16.1084 26.1931 16.1229C26.1835 16.1376 26.1791 16.155 26.1808 16.1723C26.7774 18.7742 27.8099 21.2569 28.4484 23.8587C27.9934 23.7497 27.5251 23.7075 27.0579 23.7334C26.9684 23.7751 27.5832 25.5237 27.6129 25.8281C26.5865 26.1801 25.7272 26.0249 24.5575 25.5297C24.4441 25.5416 24.3905 25.6132 24.4561 25.6669Z"
                            fill="#263238"
                          />
                          <path
                            d="M27.1295 26.1219C26.8774 26.6387 26.4974 27.0824 26.0255 27.4109C25.7599 27.5783 25.45 27.6615 25.1362 27.6496C24.45 27.6139 24.3127 27.0529 24.3247 26.486C24.3357 26.2013 24.3797 25.9189 24.456 25.6445C25.2739 26.0833 26.2103 26.2504 27.1295 26.1219Z"
                            fill="#263238"
                          />
                          <path
                            d="M26.0261 27.4102C25.7605 27.5776 25.4506 27.6608 25.1369 27.649C24.4506 27.6132 24.3133 27.0522 24.3253 26.4853C24.6715 26.4261 25.0276 26.485 25.336 26.6528C25.6446 26.8206 25.8876 27.0875 26.0261 27.4102Z"
                            fill="#FF9ABB"
                          />
                          <path
                            d="M13.5666 24.9636C13.6978 25.0592 16.628 22.3797 16.0849 17.1162C15.7686 14.0668 16.1923 11.2142 18.7226 10.9576C21.2528 10.701 22.7925 12.1511 24.6903 11.6558C26.5879 11.1605 26.6953 8.94649 28.0559 9.50148C29.4166 10.0565 30.8129 13.1299 30.8966 13.3148C30.0432 10.8561 29.0168 8.94051 28.0023 8.23036C23.0014 4.7214 13.853 6.75039 12.0925 13.0044C11.4361 15.3379 12.5103 20.7625 13.5666 24.9636Z"
                            fill="#101010"
                          />
                          <path
                            d="M23.33 25.3441C24.2472 25.033 25.0035 24.3706 25.4332 23.5025C25.8627 22.6343 25.9305 21.6313 25.6215 20.7133C25.4018 20.0663 25.005 19.4939 24.4762 19.0611C23.9474 18.6283 23.3079 18.3527 22.6301 18.2653C21.9524 18.178 21.2638 18.2825 20.6426 18.5669C20.0213 18.8515 19.4924 19.3045 19.1158 19.8747C18.7392 20.4449 18.5301 21.1093 18.5124 21.7924C18.4944 22.4755 18.6686 23.1498 19.0151 23.7388C19.3614 24.3278 19.866 24.8079 20.4717 25.1243C21.0773 25.4408 21.7595 25.581 22.4407 25.5292C22.7439 25.5056 23.0426 25.4434 23.33 25.3441ZM25.1561 20.8685C25.3562 21.4608 25.3764 22.0992 25.2139 22.7029C25.0516 23.3066 24.7138 23.8487 24.2434 24.2606C23.773 24.6725 23.1911 24.9358 22.5712 25.0171C21.9513 25.0984 21.3211 24.9941 20.7604 24.7176C20.1997 24.4409 19.7335 24.0044 19.4209 23.463C19.1082 22.9216 18.963 22.2997 19.0035 21.6757C19.0441 21.0518 19.2687 20.4539 19.649 19.9575C20.0292 19.4613 20.548 19.0888 21.1399 18.8872C21.3901 18.7992 21.651 18.7451 21.9156 18.7261C22.6167 18.6739 23.3153 18.8561 23.9018 19.2439C24.4882 19.6316 24.9294 20.203 25.1561 20.8685Z"
                            fill="#455A64"
                          />
                          <path
                            d="M30.9032 23.8363C32.3771 23.5439 33.2604 21.6999 32.8725 19.7246C32.7116 18.8271 32.2734 18.0026 31.6193 17.3674C31.3561 17.0762 31.0221 16.858 30.6496 16.7342C30.2772 16.6104 29.879 16.5852 29.4938 16.6609C29.1087 16.7365 28.7497 16.9107 28.4519 17.1662C28.154 17.4218 27.9274 17.7501 27.794 18.1192C27.4308 18.9553 27.339 19.884 27.5314 20.7749C27.8955 22.6488 29.2739 23.9735 30.6703 23.8661C30.7487 23.8621 30.8265 23.8522 30.9032 23.8363ZM31.2791 17.7254C31.8581 18.2903 32.2444 19.0231 32.3831 19.8201C32.7233 21.5328 32.0131 23.1201 30.8076 23.3589C29.6022 23.5976 28.343 22.3922 28.0029 20.6794C27.8321 19.8892 27.9114 19.0654 28.2296 18.3222C28.3439 18.0296 28.5265 17.7687 28.7621 17.561C28.9978 17.3534 29.2797 17.2052 29.5842 17.1287H29.7693C30.0486 17.1181 30.327 17.1657 30.5868 17.2684C30.8468 17.3712 31.0825 17.5268 31.2791 17.7254Z"
                            fill="#455A64"
                          />
                          <path
                            d="M25.5786 21.151L25.1967 20.8467C25.6562 20.2798 26.7901 19.4204 27.8523 20.1842L27.5658 20.584C26.617 19.8918 25.6204 21.1033 25.5786 21.151Z"
                            fill="#455A64"
                          />
                          <path
                            d="M9.7837 25.2138L9.31226 25.0825C9.37791 24.8618 9.9687 22.9342 11.0011 22.5462C12.0335 22.1584 18.5203 21.5258 18.7948 21.502L18.8426 21.9853C16.9687 22.1763 11.9201 22.7253 11.1742 23.0058C10.4282 23.2863 9.93885 24.6887 9.7837 25.2138Z"
                            fill="#455A64"
                          />
                          <path
                            d="M15.5178 23.5427C15.5178 23.5427 12.6115 19.7354 10.8749 20.7916C9.13834 21.848 11.4359 27.4455 13.5604 28.1318C13.8262 28.2393 14.111 28.292 14.3977 28.2864C14.6845 28.281 14.9671 28.2175 15.2286 28.0998C15.4901 27.9822 15.7251 27.8128 15.9193 27.6019C16.1137 27.3909 16.2634 27.1429 16.3592 26.8726L15.5178 23.5427Z"
                            fill="white"
                          />
                          <path
                            d="M11.5734 22.3799C11.5655 22.3799 11.5579 22.383 11.5523 22.3886C11.5467 22.3941 11.5436 22.4017 11.5436 22.4098C11.5436 22.4176 11.5467 22.4252 11.5523 22.4308C11.5579 22.4364 11.5655 22.4395 11.5734 22.4395C13.0653 22.9706 13.8949 24.2955 14.4917 25.68C14.3981 25.5362 14.2748 25.4143 14.13 25.3224C13.9853 25.2306 13.8224 25.171 13.6525 25.1478C13.4825 25.1246 13.3097 25.1383 13.1456 25.1878C12.9814 25.2375 12.8299 25.3218 12.7013 25.4353C12.6655 25.4652 12.7013 25.5188 12.7431 25.5128C12.9975 25.4186 13.275 25.4059 13.537 25.4765C13.799 25.5471 14.0325 25.6975 14.2052 25.9067C14.4888 26.258 14.7348 26.638 14.9391 27.0406C15.0168 27.1718 15.2495 27.0883 15.1899 26.9391V26.9093C15.1481 25.0355 13.5964 22.4992 11.5734 22.3799Z"
                            fill="#101010"
                          />
                        </svg>
                      </div>
                    ) : (
                      // Student avatar
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e9f3ff] border border-blue-100">
                        <span className="text-sm font-bold text-[#1d372b]">
                          {studentName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="et-support-turn">
                    <p className="et-support-speaker">
                      {step.role === "student" ? studentName : "EverTutor"}
                    </p>
                    <div className="et-support-text">
                      <MathText>{step.text}</MathText>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    ))}
  </div>
);

type TranscriptContentProps = ReturnType<typeof parseAttemptId> & {
  isPractice: boolean;
  studentName: string;
  isIncomplete: boolean;
  isLoading: boolean;
  error: boolean;
  supportInstances: SupportInstance[];
  onRetry: () => void;
};
const TranscriptContent = ({
  isPractice,
  sessionId: attemptSessionId,
  slideIdx: attemptSlideIdx,
  slideEndTs: attemptSlideEndTs,
  studentName,
  isIncomplete,
  isLoading,
  error,
  supportInstances,
  onRetry,
}: TranscriptContentProps) => {
  if (isPractice && attemptSessionId)
    return (
      <>
        {isPractice && attemptSessionId && (
          <div className="mt-6">
            <p className="mb-[15px] text-sm font-medium text-gray-400 italic">
              Canvas at the end of this attempt
            </p>
            <Suspense
              fallback={<div role="status">Loading saved notepad…</div>}
            >
              <NotepadSnapshotView
                sessionId={attemptSessionId}
                slideIdx={attemptSlideIdx}
                slideEndTs={attemptSlideEndTs}
              />
            </Suspense>
          </div>
        )}
        <NotepadTranscriptView
          sessionId={attemptSessionId}
          slideIdx={attemptSlideIdx}
          slideEndTs={attemptSlideEndTs}
          studentName={studentName}
        />
      </>
    );
  if (isIncomplete)
    return (
      <div className="et-support-message">
        <div className="et-support-empty">
          <p className="font-mulish font-semibold text-base text-[#25252599]">
            Support transcripts not available for incomplete bites.
          </p>
        </div>
      </div>
    );
  if (isLoading)
    return (
      <div role="status" className="et-support-message">
        <div className="relative w-16 h-16 mb-4">
          <div className="absolute inset-0 rounded-full border-4 border-[#e3eae1]"></div>
          <div className="absolute inset-0 rounded-full border-4 border-[#138c61] border-t-transparent animate-spin"></div>
        </div>
        <div className="text-[#1f3b2d] font-medium">Loading transcripts...</div>
      </div>
    );
  if (error)
    return (
      <div role="alert" className="et-support-message">
        <p>Could not load support transcripts. Please try again.</p>
        <button
          type="button"
          className="et-support-snapshot-toggle"
          onClick={onRetry}
        >
          Try again
        </button>
      </div>
    );
  if (supportInstances.length === 0)
    return (
      <p className="et-support-message">
        No instances of support are available for this attempt.
      </p>
    );
  return (
    <TranscriptInstances
      instances={supportInstances}
      studentName={studentName}
    />
  );
};

const formatTimeSpent = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const getOrdinalSuffix = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export default SupportTranscriptsModal;
